/**
 * Repair Dispatch: fault reporting contracts (gitcharger-web).
 *
 * A signed-in driver reports a broken charger. Login is required, so every
 * report carries an accountable author (reporterUserId); there is no anonymous
 * path. Reports roll up into a per-charger FaultCase, which carries the
 * lifecycle the map/panel reflects. Hosted in feedback-service.
 *
 * POST /fault/report        submit a report (returns the report + its case)
 * GET  /fault/case?chargerId=   read the current case for a charger
 */

/** What the driver says is wrong with the charger. */
export enum FaultType {
  WONT_START = 'WONT_START',
  CABLE_DAMAGED = 'CABLE_DAMAGED',
  SCREEN_ERROR = 'SCREEN_ERROR',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  PHYSICALLY_BLOCKED = 'PHYSICALLY_BLOCKED',
  SLOW_CHARGE = 'SLOW_CHARGE',
  CONNECTOR_STUCK = 'CONNECTOR_STUCK',
  OTHER = 'OTHER',
}

/**
 * Lifecycle of a per-charger fault case (the aggregate reports attach to).
 * OPEN accumulates reports; CONFIRMED once the distinct-reporter threshold is
 * hit; REPAIR_REQUESTED once the owner asks for a fix; then the technician
 * assign / estimate-approval / dispatch steps; RESOLVED when fixed. CANCELLED
 * and DISMISSED ("not broken") are terminal.
 */
export type FaultCaseStatus =
  | 'OPEN'
  | 'CONFIRMED'
  | 'REPAIR_REQUESTED'
  | 'TECHNICIAN_ASSIGNED'
  | 'TECHNICIAN_APPROVED'
  | 'RESOLVED'
  | 'CANCELLED'
  | 'DISMISSED';

/** Payload sent to POST /fault/report. */
export interface FaultReportRequest {
  stationId: number;
  chargerId: number;
  /** Optional: the specific connector, when reported at connector level. */
  connectorId?: number;
  faultType: FaultType;
  description?: string;
  /** Uploaded to the file service first; the returned URL is attached here. */
  photoUrl?: string;
  /** Firebase uid of the signed-in reporter. Required: reports are login-only. */
  reporterUserId: string;
}

/** A single fault report as returned by the API. */
export interface FaultReport extends FaultReportRequest {
  id: string;
  /** The per-charger case this report is attached to. */
  faultCaseId: string;
  /** ISO-8601 timestamp (server audit field). */
  createdDate: string;
}

/** The per-charger aggregate that the threshold counts against. */
export interface FaultCase {
  id: string;
  chargerId: number;
  stationId: number;
  status: FaultCaseStatus;
  /** Distinct reporters counted toward the confirmation threshold. */
  reporterCount: number;
  dispatchId?: string;
  firstReportedAt?: string;
  confirmedAt?: string;
  resolvedAt?: string;
  /** ISO-8601 timestamp (server audit field). */
  createdDate: string;
  /** ISO-8601 timestamp (server audit field). */
  updatedDate: string;
}

/** Response from POST /fault/report: the case, plus the report (null when the user already reported this case). */
export interface FaultReportResult {
  report: FaultReport | null;
  faultCase: FaultCase;
}
